import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Award, Download, ArrowLeft, Sparkles } from "lucide-react";
import { BRAND_ASSETS } from "@/lib/brandAssets";

export default function Certificate() {
  const { id } = useParams();
  const [cert, setCert] = useState(null);

  useEffect(() => {
    api.get(`/courses/${id}/certificate`).then((r) => setCert(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Certificate not available"));
  }, [id]);

  const downloadPdf = () => {
    // Use the browser's native print → save-as-PDF flow on the certificate component
    window.print();
  };

  if (!cert) return <div className="kb-mono text-xs text-kb-ink-muted">Loading certificate…</div>;

  return (
    <div data-testid="certificate-page">
      <Link to={`/dashboard/courses/${id}/learn`} className="kb-mono text-xs text-kb-ink-muted hover:text-kb-primary inline-flex items-center gap-2 print:hidden">
        <ArrowLeft className="w-3.5 h-3.5" /> BACK TO COURSE
      </Link>

      <div className="flex items-center justify-between mt-4 mb-6 print:hidden">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">CERTIFICATE OF COMPLETION</div>
          <h1 className="font-serif text-3xl text-kb-ink mt-1">Congratulations, <em className="italic text-kb-primary">{cert.user_name?.split(" ")[0]}</em>!</h1>
        </div>
        <button onClick={downloadPdf} className="kb-btn-primary" data-testid="cert-download-btn">
          <Download className="w-4 h-4" /> Download as PDF
        </button>
      </div>

      {/* Printable certificate */}
      <div className="certificate-paper bg-white border-4 border-kb-primary mx-auto" style={{ maxWidth: 880, padding: "60px 70px", aspectRatio: "1.414 / 1" }} data-testid="certificate-paper">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={BRAND_ASSETS.logoHorizontalDark}
              alt="KhojByte Research Lab"
              className="h-12 w-auto"
              data-testid="certificate-logo"
            />
          </div>
          <Award className="w-16 h-16 text-amber-500" strokeWidth={1.2} />
        </div>

        <div className="mt-12 text-center">
          <div className="kb-mono text-[12px] uppercase tracking-[0.3em] text-kb-ink-muted">CERTIFICATE OF COMPLETION</div>
          <div className="font-serif text-4xl lg:text-5xl text-kb-ink mt-6 italic leading-tight">{cert.user_name}</div>
          <div className="text-kb-ink-muted mt-5 text-sm uppercase tracking-wider">has successfully completed</div>
          <div className="font-serif text-2xl lg:text-3xl text-kb-primary mt-5 leading-tight">"{cert.course_title}"</div>
          <div className="text-kb-ink-muted mt-5 text-sm max-w-lg mx-auto">
            Demonstrating mastery of research workflows, methodology, and publication-ready scholarship through KhojByte's mentored LMS.
          </div>
        </div>

        <div className="mt-14 flex items-end justify-between">
          <div>
            <div className="border-t border-slate-400 pt-1 w-44">
              <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink">{cert.owner_name}</div>
              <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Course Lead</div>
            </div>
          </div>
          <div className="text-center">
            <Sparkles className="w-8 h-8 text-amber-500 mx-auto" />
            <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-1">Verified by KhojByte</div>
          </div>
          <div className="text-right">
            <div className="border-t border-slate-400 pt-1 w-44 ml-auto">
              <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink">
                Issued: {new Date(cert.issued_at).toLocaleDateString()}
              </div>
              <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
                ID: {cert.certificate_id}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          body, html { background: white !important; }
          .certificate-paper { border-color: #0a2540 !important; box-shadow: none !important; }
          [data-testid="dashboard-layout"] > aside,
          [data-testid="dashboard-layout"] > main > div:first-child,
          [data-testid="dashboard-layout"] > main > div.lg\\:hidden { display: none !important; }
        }
      `}</style>
    </div>
  );
}
